import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, AlertCircle, ArrowLeft, Mail } from 'lucide-react';

const PLANS = [
  {
    id: 'Lite',
    label: 'Lite',
    price: 'Gratis',
    description: '1 empresa, funciones esenciales',
    color: 'emerald',
  },
  {
    id: 'Plus',
    label: 'Plus',
    price: '$5.99/mes',
    description: '2 empresas, sin anuncios, más potencia',
    color: 'blue',
    popular: true,
  },
  {
    id: 'Pro',
    label: 'Pro',
    price: '$9.99/mes',
    description: 'Empresas ilimitadas, backup automático',
    color: 'indigo',
  },
];

const planStyles = {
  Lite: {
    border: 'border-emerald-300',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-400',
  },
  Plus: {
    border: 'border-blue-400',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    ring: 'ring-blue-500',
  },
  Pro: {
    border: 'border-indigo-400',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    ring: 'ring-indigo-500',
  },
};

const INVOICES_API_URL =
  process.env.REACT_APP_INVOICES_API_URL || 'http://localhost:5249';

const getInitialPlan = (plan) =>
  PLANS.some((item) => item.id === plan) ? plan : 'Plus';

export default function QuoteRequestForm() {
  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState(() => getInitialPlan(searchParams.get('plan')));
  const [form, setForm] = useState({
    customerName: '',
    email: '',
    companyName: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const res = await fetch(`${INVOICES_API_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: form.customerName,
          email: form.email,
          companyName: form.companyName,
          phone: form.phone,
          message: form.message,
          requestedPlan: selectedPlan,
        }),
      });

      if (res.status === 202 || res.ok) {
        setStatus('success');
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMessage(data?.error || 'No se pudo enviar la cotización.');
        setStatus('error');
      }
    } catch {
      setErrorMessage('Error de red. Por favor intenta de nuevo.');
      setStatus('error');
    }
  };

  const PageShell = ({ children }) => (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <nav className="border-b border-ink-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="font-bold text-[15px] text-blue-600">Facturas Simples</Link>
          <Link to="/" className="flex items-center gap-1.5 text-[14px] text-ink-500 hover:text-ink-800 transition">
            <ArrowLeft size={15} /> Volver al inicio
          </Link>
        </div>
      </nav>
      <div className="max-w-2xl mx-auto px-6 py-16">{children}</div>
    </div>
  );

  if (status === 'success') {
    return (
      <PageShell>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center py-20 text-center"
          data-testid="success-message"
        >
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
            <Check size={32} className="text-emerald-600" strokeWidth={3} />
          </div>
          <h2 className="text-2xl font-bold text-ink-950 mb-3">¡Cotización enviada!</h2>
          <p className="text-ink-500 max-w-sm">
            Revisa tu bandeja de entrada en <strong>{form.email}</strong>.
            Recibirás el PDF con los detalles del plan <strong>{selectedPlan}</strong> en unos minutos.
          </p>
          <button
            onClick={() => { setStatus('idle'); setForm({ customerName: '', email: '', companyName: '', phone: '', message: '' }); }}
            className="mt-8 text-blue-600 text-sm hover:underline"
          >
            Solicitar otra cotización
          </button>
        </motion.div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="text-center mb-10">
        <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-blue-600 mb-4">
          Cotización
        </span>
        <h1 className="text-4xl font-bold tracking-tighter text-ink-950 mb-4">
          Solicita tu cotización
        </h1>
        <p className="text-ink-500">
          Elige el plan que te interesa y te enviamos un PDF con todos los detalles.
        </p>
      </div>

      {/* Plan selector */}
      <div className="grid grid-cols-3 gap-3 mb-8" role="group" aria-label="Selecciona un plan">
        {PLANS.map((plan) => {
          const styles = planStyles[plan.id];
          const isSelected = selectedPlan === plan.id;
          return (
            <motion.button
              key={plan.id}
              type="button"
              onClick={() => setSelectedPlan(plan.id)}
              whileTap={{ scale: 0.97 }}
              aria-pressed={isSelected}
              data-testid={`plan-${plan.id.toLowerCase()}`}
              className={`relative rounded-2xl border-2 p-4 text-left transition-all focus:outline-none focus:ring-2 ${
                isSelected
                  ? `${styles.border} ${styles.bg} ring-2 ${styles.ring}`
                  : 'border-ink-200 bg-white hover:border-ink-300'
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide bg-blue-600 text-white px-2 py-0.5 rounded-full">
                  Popular
                </span>
              )}
              <div className={`text-sm font-bold mb-0.5 ${isSelected ? styles.text : 'text-ink-950'}`}>
                {plan.label}
              </div>
              <div className="text-lg font-bold text-ink-950">{plan.price}</div>
              <div className="text-[11px] text-ink-400 mt-1 leading-tight">{plan.description}</div>
            </motion.button>
          );
        })}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="customerName" className="block text-sm font-medium text-ink-700 mb-1">
              Nombre completo <span className="text-red-500">*</span>
            </label>
            <input
              id="customerName"
              name="customerName"
              type="text"
              required
              value={form.customerName}
              onChange={handleChange}
              placeholder="Tu nombre"
              className="w-full rounded-xl border border-ink-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink-700 mb-1">
              Correo electrónico <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={form.email}
              onChange={handleChange}
              placeholder="tu@empresa.com"
              className="w-full rounded-xl border border-ink-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-ink-700 mb-1">
              Empresa
            </label>
            <input
              id="companyName"
              name="companyName"
              type="text"
              value={form.companyName}
              onChange={handleChange}
              placeholder="Nombre de tu empresa"
              className="w-full rounded-xl border border-ink-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-ink-700 mb-1">
              Teléfono
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              placeholder="7777-8888"
              className="w-full rounded-xl border border-ink-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-ink-700 mb-1">
            Mensaje adicional
          </label>
          <textarea
            id="message"
            name="message"
            rows={3}
            value={form.message}
            onChange={handleChange}
            placeholder="Cuéntanos sobre tu empresa o necesidades específicas..."
            className="w-full rounded-xl border border-ink-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        <AnimatePresence>
          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3"
              data-testid="error-message"
            >
              <AlertCircle size={16} />
              {errorMessage}
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition text-[15px]"
          data-testid="submit-button"
        >
          {status === 'submitting' ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Enviando cotización…
            </>
          ) : (
            <>
              <Mail size={18} />
              Recibir cotización por email
            </>
          )}
        </button>

        <p className="text-center text-[12px] text-ink-400">
          El PDF llega en minutos. Sin compromisos.
        </p>
      </form>
    </PageShell>
  );
}
